import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { filter, type Subscription, timer } from "rxjs";

import { AuthService } from "@azm/core/services/auth";
import { DialogService } from "@azm/shared/components/overlay/dialog/services/dialog.service";
import { environment } from "@azm/environments/environment";
import {
	getIdleLastActivityKey,
	getIdleTimeoutSeconds,
	isIdleMonitoringEnabled,
	isIdlePublicRoute,
} from "@azm/core/utilities/idle-session";

const IDLE_IMMEDIATE_EVENTS = ["mousedown", "mouseup", "click", "keydown", "input", "touchstart", "pointerdown"] as const;

const IDLE_THROTTLED_EVENTS = ["mousemove", "scroll", "wheel"] as const;

@Injectable({
	providedIn: "root",
})
export class IdleSessionService {
	private readonly destroyRef = inject(DestroyRef);

	readonly #authService = inject(AuthService);
	readonly #dialogService = inject(DialogService);
	readonly #router = inject(Router);
	readonly #activityThrottleMs = 1000;
	readonly #lastActivityKey = getIdleLastActivityKey();

	#currentRoute = "/";
	#idleTimerSub: Subscription | null = null;
	#lastActivityTimestamp = Date.now();
	#lastThrottledActivityAt = 0;
	#sessionTimeoutHandled = false;
	#started = false;

	#activityHandler: (() => void) | null = null;
	#throttledActivityHandler: (() => void) | null = null;
	#storageListener: ((event: StorageEvent) => void) | null = null;
	#visibilityListener: (() => void) | null = null;

	start(): void {
		if (this.#started || typeof window === "undefined") {
			return;
		}

		this.#started = true;
		this.#currentRoute = window.location.pathname || this.#router.url || "/";
		this.#setupRouteTracking();
		this.#setupStorageListener();
		this.#setupActivityListeners();
		this.#updateMonitoringState();

		this.destroyRef.onDestroy(() => this.#teardown());
	}

	#teardown(): void {
		this.#stopIdleTimer();
		this.#teardownVisibilityCheck();
		this.#teardownActivityListeners();

		if (this.#storageListener) {
			window.removeEventListener("storage", this.#storageListener);
			this.#storageListener = null;
		}
	}

	#setupRouteTracking(): void {
		this.#router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((event) => {
				const navigation = event as NavigationEnd;
				this.#currentRoute = navigation.urlAfterRedirects || this.#router.url || window.location.pathname;
				this.#updateMonitoringState();
			});
	}

	#setupActivityListeners(): void {
		this.#activityHandler = () => this.#reportActivity();
		this.#throttledActivityHandler = () => {
			const now = Date.now();
			if (now - this.#lastThrottledActivityAt < this.#activityThrottleMs) {
				return;
			}

			this.#lastThrottledActivityAt = now;
			this.#reportActivity();
		};

		for (const eventName of IDLE_IMMEDIATE_EVENTS) {
			window.addEventListener(eventName, this.#activityHandler, { passive: true });
		}

		for (const eventName of IDLE_THROTTLED_EVENTS) {
			window.addEventListener(eventName, this.#throttledActivityHandler, { passive: true });
		}
	}

	#teardownActivityListeners(): void {
		if (this.#activityHandler) {
			for (const eventName of IDLE_IMMEDIATE_EVENTS) {
				window.removeEventListener(eventName, this.#activityHandler);
			}
			this.#activityHandler = null;
		}

		if (this.#throttledActivityHandler) {
			for (const eventName of IDLE_THROTTLED_EVENTS) {
				window.removeEventListener(eventName, this.#throttledActivityHandler);
			}
			this.#throttledActivityHandler = null;
		}
	}

	#setupStorageListener(): void {
		this.#storageListener = (event: StorageEvent) => {
			if (event.storageArea !== localStorage) {
				return;
			}

			if (event.key === environment.tokenPropertyName && event.newValue == null) {
				this.#updateMonitoringState();
				return;
			}

			if (event.key !== this.#lastActivityKey || !event.newValue) {
				return;
			}

			const timestamp = Number.parseInt(event.newValue, 10);
			if (Number.isFinite(timestamp) && timestamp > this.#lastActivityTimestamp) {
				this.#lastActivityTimestamp = timestamp;
				this.#resetIdleTimer();
			}
		};

		window.addEventListener("storage", this.#storageListener);
	}

	#reportActivity(): void {
		this.#lastActivityTimestamp = Date.now();

		if (!this.#authService.isAuthenticated) {
			return;
		}

		this.#persistLastActivity(this.#lastActivityTimestamp);
		this.#resetIdleTimer();
	}

	#persistLastActivity(timestamp: number): void {
		try {
			localStorage.setItem(this.#lastActivityKey, String(timestamp));
		} catch {}
	}

	#shouldMonitorIdle(): boolean {
		return isIdleMonitoringEnabled() && this.#authService.isAuthenticated && !isIdlePublicRoute(this.#currentRoute);
	}

	#updateMonitoringState(): void {
		const shouldMonitor = this.#shouldMonitorIdle();

		if (shouldMonitor && this.#idleTimerSub === null) {
			this.#sessionTimeoutHandled = false;
			this.#lastActivityTimestamp = Date.now();
			this.#persistLastActivity(this.#lastActivityTimestamp);
			this.#startIdleTimer();
			this.#setupVisibilityCheck();
			return;
		}

		if (!shouldMonitor && this.#idleTimerSub !== null) {
			this.#stopIdleTimer();
			this.#teardownVisibilityCheck();
		}
	}

	#startIdleTimer(): void {
		this.#stopIdleTimer();
		this.#idleTimerSub = timer(getIdleTimeoutSeconds() * 1000).subscribe(() => this.#onIdleTick());
	}

	#resetIdleTimer(): void {
		this.#lastActivityTimestamp = Date.now();

		if (this.#shouldMonitorIdle() && this.#idleTimerSub !== null) {
			this.#startIdleTimer();
		}
	}

	#stopIdleTimer(): void {
		this.#idleTimerSub?.unsubscribe();
		this.#idleTimerSub = null;
	}

	#onIdleTick(): void {
		const elapsedSeconds = (Date.now() - this.#getGlobalLastActivity()) / 1000;

		if (elapsedSeconds >= getIdleTimeoutSeconds()) {
			this.#handleSessionTimeout();
			return;
		}

		this.#resetIdleTimer();
	}

	#getGlobalLastActivity(): number {
		const raw = localStorage.getItem(this.#lastActivityKey);
		const stored = raw ? Number.parseInt(raw, 10) : Number.NaN;

		return Number.isFinite(stored) ? Math.max(stored, this.#lastActivityTimestamp) : this.#lastActivityTimestamp;
	}

	#setupVisibilityCheck(): void {
		this.#teardownVisibilityCheck();
		this.#visibilityListener = () => {
			const elapsedSeconds = (Date.now() - this.#getGlobalLastActivity()) / 1000;

			if (elapsedSeconds >= getIdleTimeoutSeconds()) {
				this.#handleSessionTimeout();
				return;
			}

			this.#resetIdleTimer();
		};

		document.addEventListener("visibilitychange", this.#visibilityListener);
		window.addEventListener("focus", this.#visibilityListener);
	}

	#teardownVisibilityCheck(): void {
		if (!this.#visibilityListener) {
			return;
		}

		document.removeEventListener("visibilitychange", this.#visibilityListener);
		window.removeEventListener("focus", this.#visibilityListener);
		this.#visibilityListener = null;
	}

	#handleSessionTimeout(): void {
		if (this.#sessionTimeoutHandled || !this.#authService.isAuthenticated) {
			return;
		}

		this.#sessionTimeoutHandled = true;
		this.#stopIdleTimer();
		this.#teardownVisibilityCheck();

		const redirectUrl = this.#authService.isInternalUser ? "/auth/login" : "/";

		this.#dialogService.closeAll();
		this.#authService.clear({ redirectUrl });
	}
}
